import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAtomValue } from "jotai";
import { userState } from "@/lib/store";
import PromoMessage from "@/components/tour/PromoMessage";
import OptionsPlatformUnlockDialog from "@/components/options-platform/unlock-dialog/OptionsPlatformUnlockDialog";

/**
 * Options Platform launch offers — one host for the three admin-managed
 * promo modals (screening.Tutorial, /admin/screening/tutorial/) plus the
 * purchase dialog they all lead to.
 *
 *   options_platform_offer_legacy — grandfathered (free legacy options)
 *       users: upgrade pitch, first month free. Server-targeted at
 *       PRODUCT_OPTIONS_LEGACY, excluded for addon holders.
 *   options_platform_offer_new — everyone else without the addon: the
 *       OPRA-fee explanation + the same offer. Server-side exclude_groups
 *       keeps it away from legacy users and subscribers.
 *   options_platform_missing_out — legacy users who declined (dismissed
 *       the offer or closed the purchase dialog): what they're passing
 *       up. Mounted only after a decline in this session; its own
 *       dismissal persistence applies after that.
 *
 * The DB promos use cta_href "#options-platform-subscribe", which
 * PromoMessage hands to onCtaAction — opening the purchase dialog here
 * instead of navigating. Mount on: /options, /s/[exch]/[ticker]/options,
 * /options-platform/news.
 */
const OptionsPlatformOfferHost = () => {
    const user = useAtomValue(userState);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [declined, setDeclined] = useState(false);

    // Subscribers need no offers, and signed-out visitors are not interrupted:
    // the locked page already sells the add-on. Stay hidden until the user has
    // loaded, so a visitor never sees the promo in the gap before we know.
    if (user?.options_platform_subscriber || !user?.subscription_plan || user.subscription_plan === "ORTEX Visitor") return null;
    const legacy = !!user?.options_legacy;
    const openSubscribe = () => setUnlockOpen(true);

    return (
        <>
            <PromoMessage tourId="options_platform_offer_legacy" onCtaAction={openSubscribe} onDismissed={() => legacy && setDeclined(true)} paywall />
            <PromoMessage tourId="options_platform_offer_new" onCtaAction={openSubscribe} paywall />
            {declined && <PromoMessage tourId="options_platform_missing_out" onCtaAction={openSubscribe} />}
            <OptionsPlatformUnlockDialog
                open={unlockOpen}
                onClose={() => {
                    setUnlockOpen(false);
                    // Closing the purchase dialog without buying counts as
                    // a decline for the follow-up modal (legacy users).
                    if (legacy) setDeclined(true);
                }}
            />
        </>
    );
};

/**
 * Inline paywall for the legacy options surfaces — replaces the free
 * OPRA sign-up prompt for users WITHOUT grandfathered access. (New users
 * can no longer unlock the old options pages for free; options data now
 * comes with the paid add-on.)
 */
export const OptionsUpsellMessage = ({ color }: { color?: string }) => {
    const [unlockOpen, setUnlockOpen] = useState(false);
    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, p: 4, textAlign: "center" }}>
            <LockOutlinedIcon sx={{ fontSize: 34, opacity: 0.6, color }} />
            <Typography variant="body1" sx={{ maxWidth: 520, color }}>
                Live options data is a premium add-on (exchange licensing means it carries its own fee). Your first month is free, then 9.90 a month: cancel anytime.
            </Typography>
            <Button variant="contained" onClick={() => setUnlockOpen(true)} sx={{ fontWeight: 700 }}>
                Unlock options data
            </Button>
            <OptionsPlatformUnlockDialog open={unlockOpen} onClose={() => setUnlockOpen(false)} />
        </Box>
    );
};

export default OptionsPlatformOfferHost;
